import React, { useEffect, useState } from "react";
import { useLocation, useParams, matchPath } from "react-router-dom";
import { useSettings } from "../contexts/SettingsContext";
import { useI18n } from "../contexts/I18nContext";
import { api } from "../lib/api";
import { buildWhatsappUrl, getWhatsappMessage } from "../lib/whatsapp";

const sanitizePhone = (p) => (p || "").replace(/[^\d+]/g, "");

export default function FloatingContact() {
  const { settings } = useSettings();
  const { lang } = useI18n();
  const { pathname } = useLocation();
  const [carName, setCarName] = useState("");

  // Detect /cars/:id route and load car name for pre-fill
  const carMatch = matchPath({ path: "/cars/:id" }, pathname);
  const carId = carMatch?.params?.id;
  useEffect(() => {
    if (!carId) { setCarName(""); return; }
    let cancelled = false;
    api.get(`/cars/${carId}`).then((r) => {
      if (!cancelled) setCarName(`${r.data.brand} ${r.data.model || r.data.name || ""}`.trim());
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [carId]);

  // Hide on admin/owner/login/review-submit screens
  if (/^\/(admin|owner|login|review\/)/.test(pathname)) return null;
  const contact = settings?.contact || {};
  const fc = settings?.floating_contact || { whatsapp_enabled: true, call_enabled: true };
  const phone = sanitizePhone(contact.phone);
  if (!phone) return null;
  if (!fc.whatsapp_enabled && !fc.call_enabled) return null;

  const waMessage = getWhatsappMessage({
    settings,
    lang,
    kind: carName ? "short" : "generic",
    vars: { car_name: carName },
  });
  const wa = buildWhatsappUrl({ phone, message: waMessage });
  const tel = `tel:${phone}`;
  const labelWa = lang === "en" ? "WhatsApp us" : "Scrie pe WhatsApp";
  const labelCall = lang === "en" ? "Call us" : "Suna acum";
  return (
    <div
      className="fixed z-40 bottom-6 right-6 flex flex-col gap-3"
      data-testid="floating-contact"
    >
      {fc.whatsapp_enabled && (
      <a
        href={wa}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={labelWa}
        className="group flex items-center gap-2 rounded-full shadow-2xl pl-4 pr-5 py-3 transition-transform hover:scale-[1.03]"
        style={{ background: "#25D366", color: "#fff" }}
        data-testid="whatsapp-btn"
      >
        <svg width="22" height="22" viewBox="0 0 32 32" fill="currentColor" aria-hidden>
          <path d="M19.11 17.32c-.27-.13-1.59-.78-1.83-.87-.25-.09-.43-.13-.61.13-.18.27-.7.87-.86 1.05-.16.18-.32.2-.59.07-.27-.13-1.14-.42-2.17-1.34-.8-.71-1.34-1.59-1.5-1.86-.16-.27-.02-.42.12-.55.12-.12.27-.32.41-.48.14-.16.18-.27.27-.45.09-.18.05-.34-.02-.47-.07-.13-.61-1.47-.83-2.01-.22-.53-.45-.45-.61-.46-.16-.01-.34-.01-.52-.01-.18 0-.47.07-.72.34-.25.27-.95.93-.95 2.27 0 1.34.97 2.63 1.1 2.81.13.18 1.9 2.9 4.61 4.07.64.28 1.15.45 1.54.57.65.21 1.24.18 1.71.11.52-.08 1.59-.65 1.81-1.28.22-.62.22-1.16.16-1.28-.07-.13-.25-.2-.52-.34zM16.02 4C9.39 4 4 9.39 4 16.02c0 2.12.55 4.18 1.6 6L4 28l6.13-1.6a11.95 11.95 0 0 0 5.89 1.5h.01c6.63 0 12.02-5.39 12.02-12.02C28.05 9.39 22.66 4 16.02 4zm0 21.94c-1.86 0-3.69-.5-5.29-1.45l-.38-.22-3.65.96.97-3.55-.25-.39a9.94 9.94 0 0 1-1.55-5.33c0-5.49 4.47-9.96 9.96-9.96s9.96 4.47 9.96 9.96-4.47 9.98-9.97 9.98z" />
        </svg>
        <span className="text-sm font-medium hidden md:inline">{labelWa}</span>
      </a>
      )}
      {fc.call_enabled && (
      <a
        href={tel}
        aria-label={labelCall}
        className="group flex items-center gap-2 rounded-full shadow-2xl pl-4 pr-5 py-3 transition-transform hover:scale-[1.03]"
        style={{ background: "var(--brand-primary)", color: "#050505" }}
        data-testid="call-btn"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
        <span className="text-sm font-medium hidden md:inline">{labelCall}</span>
      </a>
      )}
    </div>
  );
}
